import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { checkoutService, ProcessCheckoutResponse } from '@/services/checkout.service'
import type {
  CheckoutTemplate,
  Product,
  Discount,
  Order,
  CheckoutFormData,
  CustomerPrefill,
} from '@/types/checkout'
import { parseQueryParams } from '@/lib/utils'

type CheckoutStep = 'checkout' | 'payment' | 'upsell' | 'downsell' | 'processing' | 'success'

export interface UseCheckoutOptions {
  templateSlug: string
  queryString?: string
}

export interface UseCheckoutReturn {
  // Data
  template: CheckoutTemplate | undefined
  product: Product | undefined
  discount: Discount | undefined
  customerPrefill: CustomerPrefill | undefined
  order: Order | null

  // States
  step: CheckoutStep
  isLoading: boolean
  error: Error | null

  // Actions
  processCheckout: (data: CheckoutFormData) => Promise<ProcessCheckoutResponse>
  loadOrderById: (orderId: string) => Promise<Order>
  processUpsell: () => Promise<Order>
  processDownsell: () => Promise<Order>
  setStep: (step: CheckoutStep) => void
}

export function useCheckout({ templateSlug, queryString = '' }: UseCheckoutOptions): UseCheckoutReturn {
  const [step, setStep] = useState<CheckoutStep>('checkout')
  const [order, setOrder] = useState<Order | null>(null)

  const queryParams = parseQueryParams(queryString)

  // Carregar template e configurações
  const {
    data: checkoutData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['checkout-template', templateSlug, queryParams],
    queryFn: async () => checkoutService.getTemplate(templateSlug, queryParams),
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: false,
  })

  // Processar checkout
  const processCheckoutMutation = useMutation({
    mutationFn: async (data: CheckoutFormData) => {
      if (!checkoutData?.template || !checkoutData?.product) {
        throw new Error('Dados do checkout não carregados')
      }

      return checkoutService.process({
        template_id: checkoutData.template.id,
        product_id: checkoutData.product.id,
        customer: data.customer,
        payment_method: data.payment_method,
        discount_code: data.discount_code,
        metadata: {
          ...queryParams,
          template_slug: templateSlug,
        },
      })
    },
    onSuccess: (response) => {
      setOrder(response.order)

      // Verificar se tem upsell habilitado
      if (checkoutData?.template.funnel.upsell_enabled) {
        setStep('upsell')
      } else {
        setStep('payment')
      }
    },
  })

  const loadOrderMutation = useMutation({
    mutationFn: (orderId: string) => checkoutService.getOrder(orderId),
    onSuccess: (loadedOrder) => {
      setOrder(loadedOrder)
      setStep('payment')
    },
  })

  // Processar upsell
  const processUpsellMutation = useMutation({
    mutationFn: () => {
      if (!order || !checkoutData?.template.funnel.upsell_product_id) {
        throw new Error('Dados do upsell não disponíveis')
      }

      return checkoutService.processUpsell(order.id, checkoutData.template.funnel.upsell_product_id)
    },
    onSuccess: (upsellOrder) => {
      // Adicionar upsell ao pedido principal
      setOrder(upsellOrder)
      setStep('payment')
    },
  })

  // Processar downsell
  const processDownsellMutation = useMutation({
    mutationFn: () => {
      if (!order || !checkoutData?.template.funnel.downsell_product_id) {
        throw new Error('Dados do downsell não disponíveis')
      }

      return checkoutService.processDownsell(order.id, checkoutData.template.funnel.downsell_product_id)
    },
    onSuccess: (downsellOrder) => {
      setOrder(downsellOrder)
      setStep('payment')
    },
  })

  return {
    // Data
    template: checkoutData?.template,
    product: checkoutData?.product,
    discount: checkoutData?.discount,
    customerPrefill: checkoutData?.customer_prefill,
    order,

    // States
    step,
    isLoading,
    error: error as Error | null,

    // Actions
    processCheckout: processCheckoutMutation.mutateAsync,
    loadOrderById: loadOrderMutation.mutateAsync,
    processUpsell: processUpsellMutation.mutateAsync,
    processDownsell: processDownsellMutation.mutateAsync,
    setStep,
  }
}
